import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CurrencyDollar, CheckCircle, Lightning } from "@phosphor-icons/react";
import axios from "axios";
import config from "../config";

const API = `${config.BACKEND_URL}/api`;

// Load Razorpay script dynamically
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export default function BillingPage() {
  const { user, getAuthHeaders } = useAuth();
  const [usage, setUsage] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const fetchUsage = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/usage`, { headers: getAuthHeaders() });
      setUsage(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [getAuthHeaders]);

  const fetchPricing = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/billing/pricing`, { headers: getAuthHeaders() });
      setPricing(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    const init = async () => {
      await Promise.all([fetchUsage(), fetchPricing()]);
      setLoading(false);
    };
    init();
  }, [fetchUsage, fetchPricing]);

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        alert("Failed to load payment gateway. Please try again.");
        setUpgrading(false);
        return;
      }

      // Create order from backend
      const res = await axios.post(
        `${API}/billing/checkout`,
        { origin_url: window.location.origin },
        { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
      );

      const { order_id, amount, currency, razorpay_key_id, user_name, user_email } = res.data;

      // Open Razorpay Checkout Popup
      const options = {
        key: razorpay_key_id,
        amount: amount,
        currency: currency,
        name: "ChatBot Builder",
        description: "Pro Plan Subscription",
        order_id: order_id,
        prefill: {
          name: user_name || user?.name || "",
          email: user_email || user?.email || "",
        },
        theme: {
          color: "#002FA7",
        },
        handler: async function (response) {
          // Payment successful — verify on backend
          setPaymentStatus("checking");
          try {
            await axios.post(
              `${API}/billing/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
            );
            setPaymentStatus("paid");
            fetchUsage();
          } catch (err) {
            console.error(err);
            setPaymentStatus("error");
          }
        },
        modal: {
          ondismiss: function () {
            setUpgrading(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function () {
        setPaymentStatus("failed");
        setUpgrading(false);
      });
      rzp.open();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || "Failed to create checkout");
    } finally {
      setUpgrading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm font-mono uppercase tracking-[0.2em] text-[#6B7280]">Loading...</div>
      </div>
    );
  }

  const isPro = usage?.plan === "pro";

  // Display price based on location
  const priceDisplay = pricing?.display || "$49/month";
  const isIndian = pricing?.currency === "INR";

  return (
    <div className="space-y-8 max-w-2xl" data-testid="billing-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Billing</h1>
        <p className="text-sm text-[#6B7280] mt-1">Manage your subscription plan.</p>
      </div>

      {paymentStatus && (
        <div
          className={`border p-4 flex items-center gap-3 ${
            paymentStatus === "paid"
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : paymentStatus === "checking"
              ? "bg-blue-50 border-blue-200 text-blue-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
          data-testid="payment-status-banner"
        >
          {paymentStatus === "paid" && <CheckCircle size={20} weight="fill" />}
          {paymentStatus === "checking" && <Lightning size={20} weight="fill" />}
          <span className="text-sm font-medium">
            {paymentStatus === "paid" && "Payment successful! Your plan has been upgraded to Pro. 🎉"}
            {paymentStatus === "checking" && "Verifying payment..."}
            {paymentStatus === "failed" && "Payment failed. Please try again."}
            {paymentStatus === "error" && "Error verifying payment. Please contact support."}
          </span>
        </div>
      )}

      <div className="border border-[#E5E7EB]">
        <div className="p-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]/40 mb-4">Current Plan</div>
          <div className="flex items-end gap-3 mb-6">
            <span className="font-heading text-5xl font-black tracking-tighter">
              {isPro ? priceDisplay.split("/")[0] : (isIndian ? "₹0" : "$0")}
            </span>
            <span className="text-[#6B7280] text-sm pb-1">/month</span>
            <span
              className={`text-xs font-bold uppercase tracking-[0.15em] px-2 py-1 ml-2 mb-1 ${
                isPro ? "bg-[#002FA7] text-white" : "bg-[#F3F4F6] text-[#6B7280]"
              }`}
            >
              {isPro ? "PRO" : "FREE"}
            </span>
          </div>

          {/* Location Badge */}
          {pricing && !isPro && (
            <div className="mb-4 inline-flex items-center gap-1 text-xs text-[#6B7280] bg-[#F3F4F6] px-3 py-1">
              📍 {pricing.country} pricing applied
            </div>
          )}

          <div className="space-y-3 mb-8">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#6B7280]">Messages</span>
              <span className="font-mono">
                {usage?.messages_used || 0} / {isPro ? "Unlimited" : usage?.messages_limit}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#6B7280]">Websites</span>
              <span className="font-mono">
                {usage?.websites_count || 0} / {usage?.websites_limit}
              </span>
            </div>
          </div>

          {!isPro && (
            <Button
              onClick={handleUpgrade}
              disabled={upgrading}
              className="rounded-none bg-[#002FA7] hover:bg-[#001F73] text-white h-12 px-8 text-sm font-semibold transition-transform active:scale-[0.98]"
              data-testid="upgrade-pro-button"
            >
              <CurrencyDollar size={18} weight="bold" className="mr-2" />
              {upgrading ? "Opening payment..." : `Upgrade to Pro — ${priceDisplay}`}
            </Button>
          )}

          {/* Payment methods info */}
          {!isPro && (
            <p className="mt-3 text-xs text-[#6B7280]">
              {isIndian
                ? "💳 UPI, NetBanking, Cards, Wallets accepted"
                : "💳 PayPal, International Cards accepted"}
            </p>
          )}
        </div>

        {!isPro && (
          <div className="border-t border-[#E5E7EB] p-8 bg-[#F9FAFB]">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]/40 mb-4">Pro includes</div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {["Unlimited messages", "Up to 10 websites", "Custom branding", "Priority support"].map(
                (feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#0A0A0A]/80">
                    <CheckCircle size={16} weight="fill" className="text-[#002FA7]" />
                    {feature}
                  </li>
                )
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
